import { processPrintDetails } from "./Utils";
import printer from "./PrintUtility";

export default {
    methods: {
        /**
         * Handles printing sequentially for multiple print details.
         *
         * @param {Array} printDetails - Array of processed print details.
         * @returns {Promise<Array>} - Resolves with an array of failed print details.
         */
        async processPrintJobs(printDetails) {
            const failedPrints = [];

            for (const detail of printDetails) {
                try {
                    await printer.print(detail);
                } catch (error) {
                    console.error(`Printing failed for ${detail.ip_address || detail.printer_type}:`, error);

                    // Log the failure for tracking
                    failedPrints.push({
                        printer: detail.ip_address || detail.printer_type,
                        message: error.message || error || "Unknown error",
                    });
                }
            }

            return failedPrints;
        },
        onPrintSuccess(msg = null){
            if(!msg){return}
            this.$store.commit("showSnackbar", {
                message: msg,
                color: "green",
            });
        },

        /**
         * Main method to handle printing via multiple printers one by one.
         *
         * @param {Array} req - Array of print requests, each containing buffer and printer information.
         * @returns {Promise<void>} - Resolves when all prints are processed, shows errors for failed ones.
         */
        async callPrinter(req, msg = null) {
            if (!req) {
                this.onPrintSuccess(msg);
                return;
              }
            // Start the loader
            this.$root.$loader.start("Invoking print. Please wait...", {});

            try {
                // Process the print details from the request
                const printDetails = processPrintDetails(req);

                // Process the print jobs sequentially
                const failedPrints = await this.processPrintJobs(printDetails);
                if (failedPrints.length === 0) {
                    // All success
                    this.onPrintSuccess(msg);
                    return
                }

                // Display messages based on success/failure
                if (failedPrints.length === 1) {
                    // Single error: show the message directly
                    const [failure] = failedPrints; // Destructure the single failed print
                    this.$store.commit("showSnackbar", {
                        message: `Print failed for Printer: ${failure.printer}, Error: ${failure.message}`,
                        color: "error",
                    });
                    return
                }

                // Multiple errors: aggregate and show them
                const errorMessages = failedPrints
                    .map((f) => `Printer: ${f.printer}, Error: ${f.message}`)
                    .join("\n");
                this.$store.commit("showSnackbar", {
                    message: `Some prints failed:\n${errorMessages}`,
                    color: "error",
                });
            } catch (error) {
                console.error("Unexpected error during printing process:", error);
                this.$store.commit("showSnackbar", {
                    message: `Print failed: ${error.message || "Unknown error occurred."}`,
                    color: "error",
                });
                throw error; // Re-throw for further handling if needed
            } finally {
                // Stop the loader
                this.$root.$loader.stop();
            }
        },
    },
};
